import gql from 'graphql-tag';

const memberFields = `
  id
  username
  avatarUrl
  firstName
  lastName
  bgColor
  name
  email
  resumeUrl
`;

const teamFields = `
  id
  name
  description
  roleTags
  limit
  uuid
  group
  tableNumber
  submissionSet {
    award {
      name
    }
    name
    preview
    description
    modified
    websiteUrl
    mediaUrl
    githubUrl
    fileUrl
    comment
    fileName
    created
    isPrivate
    event {
      slug
    }
    awardEntered {
      id
      name
    }
    topicEntered {
      id 
      name
    }
    team {
      id
      name
      roleTags
      group
      tableNumber
      uuid
      event {
        slug
      }
      creator {
        id
      }
      members {
        id
        username
        email
        avatarUrl
        firstName
        lastName
        bgColor
        name
      }
    }
  }
  creator {
    ${memberFields}
  }
  members {
    ${memberFields}
  }
  pending {
    ${memberFields}
  }
`;

const submissionFields = ` 
  id
  name
  created
  isPrivate
  isMarked(token: $token)
  preview
  description
  modified
  websiteUrl
  mediaUrl
  githubUrl
  fileUrl
  comment
  event {
    slug
  }
  award {
    name
  }
  topicEntered {
    id 
    name
  }
  thumbsUp {
    id
    name
  }
  team {
    id
    name
    roleTags
    group
    uuid
    event {
      slug
    }
    creator {
      id
    }
    members {
      ${memberFields}
    }
  }
`;

// Queries
export const INDIVIDUAL_PARTICIPANT_LIST = gql`
  query ($slug:String!, $token:String!, $searchInput:String) {
    individualParticipantList(
      slug: $slug
      token: $token
      searchInput: $searchInput
    ) {
      userProfiles {
        email
        username
        avatarUrl
      }
    }
  }
`;

export const USER_ELIGIBLE_EVENTS_QUERY = gql`
  query eventsQuery(
    $token: String,
    $field: String,
    $search: String,
    $numPerPage: Int,
    $page: Int
  ) {
    searchEventsByToken_WRes(
      token: $token,
      field: $field,
      search: $search,
      numPerPage: $numPerPage,
      page: $page
    ) {
      code
      status
      totalPages
      events {
        featured
        id
        start
        end
        slug
        location
        coverImageUrl
        bannerBg
        name
        isPrivate
        type
        timezone
        tagsSet
        creator{
          id
        }
        managers{
          id
        }
        eventTickets {
          id
          type
          description
          status
          start
          end
          quantities
          sold
          price
          ordering
        }
      }
    }
  }
`;

export const USER_PROFILE_QUERY = gql`
  query userProfileQuery($token: String!, $slug: String) {
    searchUserProfilesByUserToken_WRes(token: $token) {
      code
      status
      userProfile{
        id
        roleSet(slug: $slug) {
          title
          event {
            id
          }
        }
      }
    }
  }
`;

export const EVENT_DETAIL_QUERY = gql`
  query eventDetailQuery ($slug: String!){
    searchEventBySlug(slug: $slug){
      id
      isPrivate
      isScreening
      containStreaming
      slug
      type
      name
      enableMentor
      coverImageUrl
      bannerBg
      content
      isHomepageUrl
      externalSignupUrl
      contentJson
      timezone
      start
      end
      subDeadline
      maxTeamMembers
      location
      resourceSet {
        name
        contentUrl
        fileUrl
        content
        preview
        description
      }
      creator {
        id
      }
      managers {
        id
      }
      # old version participants
      participants {
        id
      }
      # old version judges
      judges {
        id
      }
      teamSet {
        ${teamFields}
      }
      awardSet {
        id
        default
        name
        description
        websiteUrl
        ordering
      }
      topicSet {
        id
        default
        name
        description
        websiteUrl
        ordering
      }
      judgingcriteriaSet {
        id
        title
        minimum
        maximum
        ordering
      }
      eventTickets {
        id
        type
        description
        status
        start
        end
        quantities
        sold
        price
        ordering
      }
    }
  }
`;

export const EVENT_SUBMISSIONS_QUERY = gql`
  query eventSubmissionsQuery($eventSlug: String, $token: String) {
    searchSubmissionsByEvent_WRes(eventSlug: $eventSlug) {
      status
      code
      message
      submissions {
        ${submissionFields}
      }
    }
  }
`;

// Mutations
export const JOIN_EVENT_MUTATION = gql`
  mutation joinEvent($token: String, $eventSlug: String) {
    joinEvent(joinInput: { token: $token, eventSlug: $eventSlug }) {
      status
      code
      message
    }
  }
`;

export const KICK_TEAM_MUTATION = gql`
  mutation(
    $teamId: Int!,
    $token: String!,
    $memberId: Int!,
  ){
    kickTeam(data: {
      token: $token,
      teamId: $teamId,
      memberId: $memberId,
    }) {
      status
      code
      message
      team{
        ${teamFields}
      }
    }
  }
`;

export const ACCEPT_TEAM_MUTATION = gql`
  mutation(
    $teamId: Int!,
    $token: String!,
    $memberId: Int!,
  ){
    acceptTeam(data: {
      token: $token,
      teamId: $teamId,
      applicantId: $memberId,
    }) {
      status
      code
      message
      team{
        ${teamFields}
      }
    }
  }
`;

export const REJECT_TEAM_MUTATION = gql`
  mutation(
    $teamId: Int!,
    $token: String!,
    $memberId: Int!,
  ){
    rejectTeam(data: {
      token: $token,
      teamId: $teamId,
      applicantId: $memberId,
    }) {
      status
      code
      message
      team{
        ${teamFields}
      }
    }
  }
`;

export const CREATE_UPDATE_TEAM = gql`
  mutation(
    $token: String!
    $eventSlug: String!
    $name: String
    $description: String
    $limit: Int!
    $roleTags: [String]
    $teamId: Int
    $idea: String
    $ideaChatId: String
  ){
    createUpdateTeam(data: {
      token: $token,
      eventSlug: $eventSlug,
      name: $name,
      description: $description,
      limit: $limit,
      roleTags: $roleTags,
      teamId: $teamId,
      idea:$idea,
      ideaChatId:$ideaChatId
    }) {
      status
      code
      message
      team{
        ${teamFields}
      }
    }
  }
`;

export const DELETE_TEAM = gql`
  mutation($token: String!, $teamId: Int!) {
    deleteTeam(data: { token: $token, teamId: $teamId }) {
      status
      code
      message
    }
  }
`;

export const JOIN_TEAM = gql`
  mutation(
    $token: String!
    $teamId: Int!
  ){
    applyTeam (data: {
      token: $token,
      teamId: $teamId,
    }) {
      status
      code
      message
      team{
        ${teamFields}
      }
    }
  }
`;

export const LEAVE_TEAM = gql`
  mutation($teamId: Int!, $token: String!) {
    leaveTeam(data: { token: $token, teamId: $teamId }) {
      status
      code
      message
    }
  }
`;

export const CHECK_EVENT_SLUG = gql`
  mutation($slug: String!) {
    checkEventSlug(slug: $slug) { status }
  }
`;

// idを追加する（オプショナル、既存のsubmissionのupdateの場合はそのsubmission id をpassする）
export const SUBMIT_PROJECT = gql`
  mutation submitProject(
    $token: String
    $eventSlug: String
    $name: String
    $fileName: String
    $description: String
    $preview: String
    $fileUrl: String
    $websiteUrl: String
    $githubUrl: String
    $mediaUrl: String
    $awardsEntered: [Int]
    $topicsEntered: [Int]
    $targetRoundNumber: Int
  ){
    eventSubmission(data: {
      token: $token
      eventSlug: $eventSlug
      name: $name
      fileName: $fileName
      description: $description
      preview: $preview
      fileUrl: $fileUrl
      websiteUrl: $websiteUrl
      githubUrl: $githubUrl
      mediaUrl: $mediaUrl
      awardsEntered: $awardsEntered
      topicsEntered: $topicsEntered
      targetRoundNumber: $targetRoundNumber
    }) {
      status
      code
      message
      submission {
        ${submissionFields}
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/events/graphql.js